import { useApi } from '~/api'
import { defineService } from '~/api/models/helpers'
import type { AssetStackGroup } from '~/types/asset'
import type { AssetPresetSyncOptions } from '~/types/configurationPreset'
import type { AssetFilters } from '~/types/filters'
import type { AuthArgs } from '~/api/models/index'
import type { PlaceholderDataFn } from '~/api/models/helpers/types'

interface AssetsActionsArgs {
  assetIds: number[]
  assetTypeIds?: string[]
  context: string
  assetTypeId?: string
}

const queries = {
  getAll: {
    queryFn: (filters: Ref<AssetFilters>) => useApi().assets.getFilteredAssets(filters.value),
    queryKey: (filters: Ref<AssetFilters>) => ['filteredAssets', filters.value],
    options: {
      enabled: (filters: Ref<AssetFilters>) => (filters.value?.assetTypeIds?.length ?? 0) > 0
    }
  },
  getById: {
    queryFn: (id: ComputedRef<number>) => useApi().assets.getAssetById(id.value),
    queryKey: (id: ComputedRef<number>) => ['assetById', id.value],
    options: {
      enabled: (id: ComputedRef<number>) => !!id.value,
      staleTime: () => 0,
      refetchOnMount: () => 'always' as const
    }
  },
  getAllIds: {
    queryFn: (filters: ComputedRef<AssetFilters | null>) => {
      if (!filters.value) {
        return Promise.resolve([] as number[])
      }
      return useApi().assets.getFilteredAssetIds(filters.value)
    },
    queryKey: (filters: ComputedRef<AssetFilters | null>) => ['filteredAssetIds', filters.value],
    options: {
      enabled: (filters: ComputedRef<AssetFilters | null>) => !!filters.value
    }
  },
  getAssetTypes: {
    queryFn: () => useApi().assets.getAssetTypes(),
    queryKey: () => ['assetTypes']
  },
  getGroupedCount: {
    queryFn: (groupBy: Ref<AssetStackGroup>) => useApi().assets.getGroupedAssetsCount(groupBy.value),
    queryKey: (groupBy: Ref<AssetStackGroup>) => ['groupedAssetsCount', groupBy.value]
  },
  getDistribution: {
    queryFn: (filters: ComputedRef<AssetFilters | null>) => {
      if (!filters.value) {
        return Promise.resolve(null)
      }
      return useApi().assets.getFilteredAssetDistribution(filters.value)
    },
    queryKey: (filters: ComputedRef<AssetFilters | null>) => ['assetDistribution', filters.value],
    options: {
      enabled: (filters: ComputedRef<AssetFilters | null>) => !!filters.value
    }
  },
  getAssetsSTC: {
    queryFn: () => useApi().assets.getAssetsSTC(),
    queryKey: () => ['assetTelemetries']
  },
  getAssetsActionsByAssetIds: {
    queryFn: (args: ComputedRef<AssetsActionsArgs>) =>
      useApi().assets.getAssetsActionsByAssetIds(args.value.assetIds, args.value.context, args.value.assetTypeIds),
    queryKey: (args: ComputedRef<AssetsActionsArgs>) => [
      'assetsActionsByAssetIds',
      String(args.value.assetTypeId ?? ''),
      ...args.value.assetIds.map(String),
      args.value.context,
      ...(args.value.assetTypeIds ?? [])
    ],
    options: {
      enabled: (args: ComputedRef<AssetsActionsArgs>) =>
        args.value.assetIds.length > 0 || (args.value.assetTypeIds?.length ?? 0) > 0,
      // Keeps the previous asset's actions rendered while the next asset loads so the panel does not
      // collapse and reflow. Gated on the asset type so another type's controls are never shown.
      placeholderData:
        (args: ComputedRef<AssetsActionsArgs>): PlaceholderDataFn =>
        (previousData, previousQuery) =>
          previousQuery?.queryKey?.[1] === String(args.value.assetTypeId ?? '') ? previousData : undefined
    }
  },
  getMapIconColors: {
    queryFn: () => useApi().assets.getMapIconColors(),
    queryKey: () => ['mapIconColors']
  },
  getAlertLevelColors: {
    queryFn: () => useApi().assets.getAlertLevelColors(),
    queryKey: () => ['alertLevelColors']
  },
  getMapIconConfigs: {
    queryFn: () => useApi().assets.getMapIconConfigs(),
    queryKey: () => ['mapIconConfigs']
  }
} as const

const mutations = {
  syncPreset: {
    request: ({ assetId, ...options }: AssetPresetSyncOptions & { assetId: number }) =>
      useApi().assets.syncAssetPreset(assetId, options),
    cacheKey: () => [['groupPresetStatus'], ['groupAssets'], ['assetById']]
  }
} as const

export type AssetService = ReturnType<typeof defineService<typeof queries, typeof mutations>>

export const registerService = (authArgs: AuthArgs): AssetService =>
  defineService(authArgs, {
    queries,
    mutations
  })
